import type {DigitalAssetCollectionADto} from '@cohort/admin-schemas/digitalAssetCollection';
import type {PerkADto, PerksOrderByADto} from '@cohort/admin-schemas/perk';
import {usePerkIntegrations} from '@cohort/merchants/apps/usePerkIntegrations';
import Button from '@cohort/merchants/components/buttons/Button';
import {EmptyContentContainer} from '@cohort/merchants/components/EmptyContentContainer';
import type {FilterOption} from '@cohort/merchants/components/filters/Filters';
import Filters from '@cohort/merchants/components/filters/Filters';
import SingleChoiceFilter from '@cohort/merchants/components/filters/SingleChoiceFilter';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@cohort/merchants/components/modals/Dialog';
import {PerkListItem} from '@cohort/merchants/components/perks/PerkListItem';
import {usePerks} from '@cohort/merchants/hooks/api/Perks';
import type {PaginationFilters} from '@cohort/merchants/hooks/filters/basePageFilters';
import type {PerksListFilters} from '@cohort/merchants/hooks/filters/perksListFilters';
import {
  integrationFilter,
  sortFilter,
  typeFilter,
  usePerksListFilters,
} from '@cohort/merchants/hooks/filters/perksListFilters';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {usePerkUtils} from '@cohort/merchants/hooks/usePerkUtils';
import {getPerkCreateRoute} from '@cohort/merchants/lib/Pages';
import {
  trackDigitalAssetCollectionPerksAssociationsModalAppFilterChanged,
  trackDigitalAssetCollectionPerksAssociationsModalClosed,
  trackDigitalAssetCollectionPerksAssociationsModalSortingChanged,
  trackDigitalAssetCollectionPerksAssociationsModalSubmit,
  trackDigitalAssetCollectionPerksAssociationsModalTypeFilterChanged,
} from '@cohort/merchants/lib/Tracking';
import type {PerkIntegrationId} from '@cohort/shared/schema/apps';
import type {PerkType} from '@cohort/shared/schema/common/perks';
import {Desktop, FileMagnifyingGlass, Tag} from '@phosphor-icons/react';
import React, {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {shallow} from 'zustand/shallow';

interface AssociatePerkModalProps {
  show: boolean;
  handleOnClose: () => void;
  digitalAssetCollection: DigitalAssetCollectionADto;
  handleOnAdd: (perks: PerkADto[]) => void;
  hiddenPerkIds: string[];
}

interface AppsFilterProps {
  digitalAssetCollection: DigitalAssetCollectionADto;
  hiddenPerkIds: string[];
  merchantId: string;
  filters: PaginationFilters & PerksListFilters;
}

const AppsFilter: React.FC<AppsFilterProps> = ({
  hiddenPerkIds,
  merchantId,
  digitalAssetCollection,
  filters,
}) => {
  const {t} = useTranslation('pages', {
    keyPrefix: 'digitalAssetCollections.digitalAssetCollection.perks.associatePerkModal',
  });
  const {getPerkTypeData} = usePerkUtils();
  const {getPerkIntegration, getPerkIntegrationTitle} = usePerkIntegrations();

  const integrationFilterOptions = integrationFilter.map(integrationId => {
    return {
      value: integrationId,
      label: getPerkIntegrationTitle(getPerkIntegration(integrationId)),
      active: filters.integrations ? filters.integrations.includes(integrationId) : false,
    };
  });

  const typeFilterOptions = typeFilter.map(type => {
    return {
      value: type,
      label: getPerkTypeData(type).title,
      active: filters.types ? filters.types.includes(type) : false,
    };
  });

  const {data} = usePerks(merchantId, {
    page: 1,
    pageSize: 100,
    orderBy: '-updatedAt',
    statuses: ['live'],
  });
  const [, perks] = data ?? [];

  const availableIntegrations = perks
    ?.filter(p => !hiddenPerkIds.includes(p.id))
    .map(p => p.integration?.perkIntegrationId);
  const availableIntegrationsFilterOptions = integrationFilterOptions.filter(
    option => availableIntegrations?.includes(option.value)
  );

  const availableTypes = perks?.filter(p => !hiddenPerkIds.includes(p.id)).map(p => p.type);
  const availableTypesFilterOptions = typeFilterOptions.filter(
    option => availableTypes?.includes(option.value)
  );

  return (
    <Filters
      filters={[
        {
          label: t('labelApplication'),
          options: availableIntegrationsFilterOptions,
          type: 'multiple',
          icon: <Desktop className="mr-1.5 inline-block h-4 w-4" />,
          onChange: (options: FilterOption[]) => {
            const activeOptions = options
              .filter(option => option.active)
              .map(option => option.value) as PerkIntegrationId[];

            trackDigitalAssetCollectionPerksAssociationsModalAppFilterChanged(
              digitalAssetCollection,
              activeOptions
            );
            filters.setIntegrations(activeOptions);
          },
        },
        {
          label: t('labelType'),
          options: availableTypesFilterOptions,
          type: 'multiple',
          icon: <Tag className="mr-1.5 inline-block h-4 w-4" />,
          onChange: (options: FilterOption[]) => {
            const activeOptions = options
              .filter(option => option.active)
              .map(option => option.value) as PerkType[];
            trackDigitalAssetCollectionPerksAssociationsModalTypeFilterChanged(
              digitalAssetCollection,
              activeOptions
            );
            filters.setTypes(activeOptions);
          },
        },
      ]}
    />
  );
};

const AssociatePerkModal: React.FC<AssociatePerkModalProps> = ({
  show,
  handleOnClose,
  digitalAssetCollection,
  handleOnAdd,
  hiddenPerkIds,
}) => {
  const {t} = useTranslation('pages', {
    keyPrefix: 'digitalAssetCollections.digitalAssetCollection.perks.associatePerkModal',
  });
  const navigate = useNavigate();
  const [addedPerks, setAddedPerks] = useState<PerkADto[]>([]);
  const filters = usePerksListFilters(/^(\/test)?\/digital-assets\/.*\/perks$/u);
  const {merchantId} = useUserSessionStore(
    store => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      organization: store.organization!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      merchantId: store.merchantId!,
    }),
    shallow
  );

  // i18nOwl-ignore [sortBy.-updatedAt, sortBy.internalName]
  const sortingOptions = sortFilter.map(sort => ({
    value: sort,
    label: t(`sortBy.${sort}`),
    active: filters.sort === sort,
  }));

  const {data} = usePerks(merchantId, {
    page: 1,
    pageSize: 100,
    orderBy: filters.sort,
    integrations: filters.integrations,
    types: filters.types,
    statuses: ['live'],
  });
  const [, perks] = data ?? [];

  const onSelectPerk = (perk: PerkADto): void => {
    if (!addedPerks.some(p => p.id === perk.id)) {
      setAddedPerks([...addedPerks, perk]);
    } else {
      setAddedPerks(addedPerks.filter(p => p.id !== perk.id));
    }
  };

  const handleClose = (): void => {
    trackDigitalAssetCollectionPerksAssociationsModalClosed(digitalAssetCollection);
    filters.setIntegrations([]);
    filters.setSort('updatedAt');
    setAddedPerks([]);
    handleOnClose();
  };

  const displayedPerks = perks?.filter(p => !hiddenPerkIds.includes(p.id));

  if (show !== true) {
    return <Fragment />;
  }

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="w-[80vw] max-w-[1000px]" displayCloseButton={false}>
        <DialogHeader className="space-y-0">
          <div className="mb-12 flex flex-row items-center justify-between">
            <h1 className="text-2xl font-semibold">{t('labelAddPerksToDigitalAssetCollection')}</h1>
            <Button onClick={() => navigate(getPerkCreateRoute().path)}>
              {t('buttonCreatePerk')}
            </Button>
          </div>
          <div className="mb-4 flex flex-row items-center justify-between">
            <AppsFilter
              digitalAssetCollection={digitalAssetCollection}
              hiddenPerkIds={hiddenPerkIds}
              merchantId={merchantId}
              filters={filters}
            />
            <SingleChoiceFilter
              options={sortingOptions}
              onChange={(option: FilterOption) => {
                trackDigitalAssetCollectionPerksAssociationsModalSortingChanged(
                  digitalAssetCollection,
                  option.value
                );
                filters.setSort(option.value as PerksOrderByADto);
              }}
              prefix={t('sortBy.title')}
            />
          </div>
        </DialogHeader>
        <DialogBody>
          {displayedPerks?.length === 0 ? (
            <EmptyContentContainer
              icon={<FileMagnifyingGlass size={40} className="text-slate-400" />}
              text={t('labelEmpty')}
              description={t('placeholderNoPerksAvailable')}
            />
          ) : (
            <div className="flex w-full flex-col border-slate-100">
              {displayedPerks?.map(perk => (
                <PerkListItem key={perk.id} perk={perk} handleOnSelect={onSelectPerk} />
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter className="justify-end">
          <div className="flex flex-row items-center space-x-4">
            <Button variant="secondary" onClick={handleClose}>
              {t('buttonCancel')}
            </Button>
            <Button
              data-testid="associate-modal-submit"
              disabled={addedPerks.length === 0}
              onClick={() => {
                trackDigitalAssetCollectionPerksAssociationsModalSubmit(digitalAssetCollection);
                handleOnAdd(addedPerks);
                handleClose();
              }}
            >
              {addedPerks.length === 0
                ? t('buttonAddToDigitalAssetCollectionNoPerk')
                : t('buttonAddToDigitalAssetCollection', {count: addedPerks.length})}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssociatePerkModal;
